/// <reference types="node" />
/// <reference types="react" />
/// <reference types="react/jsx-runtime" />

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Flex,
  VStack,
  Input,
  Text,
  useToast,
  Alert,
  AlertIcon,
  IconButton,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  useDisclosure,
  Button,
  Textarea,
  SlideFade,
  Slide,
  Select,
  Input as SearchInput,
  Checkbox,
  CheckboxGroup,
  Stack,
  Divider,
  Spinner,
  Tooltip,
  Tag,
  TagLabel,
  Progress,
} from '@chakra-ui/react';
import { FiSend, FiMenu, FiPlus, FiLogOut, FiSettings, FiSearch, FiMic, FiMicOff, FiMessageCircle, FiEdit, FiFileText, FiCheckCircle, FiList, FiInfo, FiEdit3, FiMessageSquare, FiRepeat, FiTrendingUp, FiPieChart, FiZap } from 'react-icons/fi';
import Message from './Message';
import ConversationSidebar, { ConversationSidebarRefType } from './ConversationSidebar';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LOGO_URL, APP_NAME } from '../constants/branding';
// Import new API hooks
import { 
  useSettingsApi,
  useConversationApi,
  useFilterApi,
  useSpeechApi,
  UserSettings,
  Company,
  Board,
  ChatMessage as ApiChatMessage,
  TranscriptionResult
} from '../api';
// Add useApi import
import { useApi } from '../api/api';
import PromptModal from './PromptModal';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string | {
    content: string;
    conversation_id: string;
    message_id: number | string;
  };
  timestamp: string;
  is_new_chat?: boolean;
}

interface ChatSession {
  id: string;
  title: string;
  timestamp: string;
}

interface ChatResponse {
  type: string;
  data: {
    content: string;
    conversation_id: string;
    message_id: number;
  };
}

// Add interface for JWT payload
interface JWTPayload {
  sub: string;  // username
  user_id: number;
  exp: number;
}

const getMessageContent = (content: string | { content: string; conversation_id: string; message_id: number | string }): string => {
  return typeof content === 'string' ? content : content.content;
};

const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [showDisconnected, setShowDisconnected] = useState(false);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { logout, getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [userId, setUserId] = useState<number | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [savedCustomPrompt, setSavedCustomPrompt] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);
  const [selectedBoards, setSelectedBoards] = useState<number[]>([]);
  const [companySearch, setCompanySearch] = useState('');
  const [boardSearch, setBoardSearch] = useState('');
  const [companyPage, setCompanyPage] = useState(1);
  const [boardPage, setBoardPage] = useState(1);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [hasMoreCompanies, setHasMoreCompanies] = useState(true);
  const [hasMoreBoards, setHasMoreBoards] = useState(true);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isTranscribing, setIsTranscribing] = useState<boolean>(false);
  const [transcript, setTranscript] = useState<string>('');
  const deepgramSocket = useRef<WebSocket | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  
  const sidebarBg = useColorModeValue('gray.50', 'gray.900');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const settingsBg = useColorModeValue('white', 'gray.800');
  const messageContainerBg = useColorModeValue('white', 'gray.900');
  const headerBg = useColorModeValue('white', 'gray.900');
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const cardHoverBg = useColorModeValue('gray.100', 'gray.700');
  const floatingBtnBg = useColorModeValue('white', 'gray.800');

  const sidebarRef = useRef<ConversationSidebarRefType>(null);
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const [expandedGroups, setExpandedGroups] = useState<number[]>([]);

  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [isProcessingAudio, setIsProcessingAudio] = useState<boolean>(false);

  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioTranscript, setAudioTranscript] = useState<string | null>(null);

  const wsBaseUrl = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('http', 'ws') : 'ws://api:8889';
  const wsFullUrl = userId ? `${wsBaseUrl}/ws/user_${userId}` : null;
  console.log('WebSocket URL:', wsFullUrl);
  const { lastMessage, connectionStatus, sendMessage } = useWebSocket(wsFullUrl);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleGroupExpansion = (groupIndex: number, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (expandedGroups.includes(groupIndex)) {
      setExpandedGroups(expandedGroups.filter(index => index !== groupIndex));
    } else {
      setExpandedGroups([...expandedGroups, groupIndex]);
    }
  };

  const suggestionGroups = [
    {
      name: "Ticket Operations",
      icon: FiFileText,
      items: [
        { text: "Find all open tickets for today", icon: FiList },
        { text: "View tickets closed today", icon: FiCheckCircle },
        { text: "Show me my assigned tickets", icon: FiMessageCircle },
        { text: "Generate a summary for this ticket", icon: FiFileText },
        { text: "Show detailed ticket information", icon: FiInfo },
        { text: "Update the status of this ticket", icon: FiEdit3 },
        { text: "Add a new note to this ticket", icon: FiMessageSquare },
      ]
    },
    {
      name: "Analytics",
      icon: FiPieChart,
      items: [
        { text: "Identify recurring ticket issues in this company", icon: FiRepeat },
        { text: "Analyze top issues on this board", icon: FiTrendingUp },
        { text: "Show most frequent issues for this company", icon: FiPieChart },
      ]
    }
  ];

  const { fetchUserSettings, saveUserSettings } = useSettingsApi();
  const { loadConversation: apiLoadConversation, renameConversation, deleteConversation } = useConversationApi();
  const { fetchCompanies: apiFetchCompanies, fetchBoards: apiFetchBoards } = useFilterApi();
  const { getDeepgramToken, convertFloat32ToInt16, getSupportedMimeType, transcribeAudio, refreshPresignedUrl } = useSpeechApi();

  useEffect(() => {
    const getUserId = async () => {
      try {
        const token = await getAccessToken();
        const payload = JSON.parse(atob(token.split('.')[1])) as JWTPayload;
        if (!payload.user_id) {
          console.error('No user_id found in token payload');
          return;
        }
        setUserId(payload.user_id);
        console.log('Set user ID:', payload.user_id);
        
        fetchUserSettingsData(payload.user_id);
      } catch (error) {
        console.error('Error getting user ID:', error);
        toast({
          title: 'Error',
          description: 'Failed to get user information',
          status: 'error',
          duration: 5000,
        });
      }
    };
    getUserId();
  }, [getAccessToken, toast]);

  const fetchUserSettingsData = async (userId: number) => {
    try {
      const settings = await fetchUserSettings(userId);
      
      setCustomPrompt(settings.custom_prompt);
      setSavedCustomPrompt(settings.custom_prompt);
      setSelectedCompanies(settings.selected_companies);
      setSelectedBoards(settings.selected_boards);
      
      console.log('Loaded user settings:', settings);
    } catch (error) {
      console.error('Error fetching user settings:', error);
    }
  };

  useEffect(() => {
    if (!lastMessage) return;

    try {
      const data = JSON.parse(lastMessage.data);
      
      if (data.type === 'agent_updated') {
        console.log('Agent updated:', data.data.agent_name);
      }
      else if (data.type === 'chat_response_chunk') {
        const { content, conversation_id, message_id, is_complete } = data.data;
        
        if (!is_complete) {
          setMessages(prev => {
            const existingMessageIndex = prev.findIndex(msg => 
              msg.role === 'assistant' && 
              (
                (typeof msg.content === 'object' && msg.content.message_id === message_id) ||
                (message_id === -1 && prev.length > 0 && msg === prev[prev.length - 1] && msg.role === 'assistant')
              )
            );
            
            if (existingMessageIndex >= 0) {
              const newMessages = [...prev];
              const existingMessage = newMessages[existingMessageIndex];
              
              if (typeof existingMessage.content === 'object') {
                if (!content || !content.trim()) {
                  return prev;
                }
                
                existingMessage.content = {
                  ...existingMessage.content,
                  content: content
                };
              }
              
              return newMessages;
            } else {
              if (!content || !content.trim()) {
                return prev;
              }
              
              return [
                ...prev,
                {
                  role: 'assistant',
                  content: {
                    content,
                    conversation_id,
                    message_id: message_id !== -1 ? message_id : 'temp_' + Date.now()
                  },
                  timestamp: new Date().toISOString()
                }
              ];
            }
          });
        } else {
          if (message_id !== -1) {
            setMessages(prev => {
              const newMessages = [...prev];
              const lastAssistantIndex = newMessages.findIndex(msg => 
                msg.role === 'assistant' && 
                typeof msg.content === 'object' && 
                (typeof msg.content.message_id === 'string' && msg.content.message_id.startsWith('temp_'))
              );
              
              if (lastAssistantIndex >= 0) {
                const lastMessage = newMessages[lastAssistantIndex];
                if (typeof lastMessage.content === 'object') {
                  lastMessage.content = {
                    ...lastMessage.content,
                    message_id: message_id
                  };
                }
              }
              
              return newMessages;
            });
          }
          
          console.log('Stream completed for message:', message_id);
        }
      }
      else if (data.type === 'chat_response') {
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: data.data,
            timestamp: new Date().toISOString()
          }
        ]);
        
        if (!currentConversationId) {
          setCurrentConversationId(data.data.conversation_id);
        }
      }
      else if (data.type === 'title_updated') {
        if (sidebarRef.current) {
          sidebarRef.current.fetchConversations(0, true);
        }
      }
    } catch (e) {
      console.error('Error processing WebSocket message:', e);
    }
  }, [lastMessage, currentConversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (connectionStatus === 'Connected') {
      setShowDisconnected(false);
      toast({
        title: 'Connected to chat server',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } else if (connectionStatus === 'Disconnected') {
      setShowDisconnected(true);
    }
  }, [connectionStatus, toast]);

  const loadConversation = async (conversationId: string) => {
    if (loadingConversation) return;
    
    setLoadingConversation(true);
    try {
      const chatMessages = await apiLoadConversation(conversationId);
      
      const processedMessages = await Promise.all(chatMessages.map(async (msg) => {
        if (msg.attachments_url && msg.attachments_type && msg.attachments_type.startsWith('audio/')) {
          const s3Key = msg.attachments_url.split('/').pop();
          
          let presignedUrl = null;
          if (s3Key) {
            try {
              const result = await refreshPresignedUrl(s3Key);
              if ('presigned_url' in result) {
                presignedUrl = result.presigned_url;
              }
            } catch (error) {
              console.error('Error refreshing pre-signed URL:', error);
            }
          }
          
          return {
            ...msg,
            content: JSON.stringify({
              type: 'audio',
              s3Url: msg.attachments_url,
              presigned_url: presignedUrl,
              s3_key: s3Key,
              transcription: msg.content
            })
          };
        }
        return msg;
      }));
      
      setMessages(processedMessages);
      setCurrentConversationId(conversationId);
      
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error loading conversation:', error);
      toast({
        title: 'Error',
        description: 'Failed to load conversation history',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoadingConversation(false);
    }
  };

  const handleSelectConversation = (conversationId: string) => {
    if (conversationId === currentConversationId) return;
    loadConversation(conversationId);
  };

  const handleNewChat = () => {
    setMessages([]);
    setCurrentConversationId(null);
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
    sendMessage(JSON.stringify({
      type: 'new-chat',
      question: '',
      context: []
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !userId) return;

    if (isRecording) {
      stopRecording();
    }

    const isNewChat = !currentConversationId;

    const newMessage: ChatMessage = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString()
    };

    setMessages((prev: ChatMessage[]) => [...prev, newMessage]);
    
    const context = messages.map((msg: ChatMessage) => ({
      role: msg.role,
      content: getMessageContent(msg.content),
      timestamp: msg.timestamp
    }));

    console.log('Sending message with conversation ID:', currentConversationId);

    const messageData: {
      type: string;
      question: string;
      context: any[];
      user_id: number;
      custom_prompt: string;
      conversation_id?: string;
    } = {
      type: 'chat',
      question: input,
      context: context.slice(-5),
      user_id: userId,
      custom_prompt: savedCustomPrompt,
    };

    if (currentConversationId) {
      messageData.conversation_id = currentConversationId;
    }

    sendMessage(JSON.stringify(messageData));
    setInput('');
    
    if (isNewChat) {
      setTimeout(() => {
        if (sidebarRef.current) {
          sidebarRef.current.fetchConversations(0, true);
        }
      }, 1000);
    }
  };

  const handleFeedback = (messageId: number | string, rating: number, feedbackText?: string) => {
    sendMessage(JSON.stringify({
      type: 'feedback',
      message_id: messageId,
      rating: rating,
      feedback_text: feedbackText
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSettingsOpen = () => {
    setIsSettingsOpen(true);
    setCustomPrompt(savedCustomPrompt);
  };

  const handleSettingsClose = () => {
    setIsSettingsOpen(false);
  };

  const handleSaveSettings = async () => {
    setSavedCustomPrompt(customPrompt);
    
    sendMessage(JSON.stringify({
      type: 'update_settings',
      custom_prompt: customPrompt,
      user_id: userId,
      selected_companies: selectedCompanies,
      selected_boards: selectedBoards
    }));
    
    try {
      const settings: UserSettings = {
        custom_prompt: customPrompt,
        selected_companies: selectedCompanies,
        selected_boards: selectedBoards
      };
      
      const success = await saveUserSettings(userId as number, settings);
      
      if (success) {
        toast({
          title: 'Settings saved',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings to database:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
    
    setIsSettingsOpen(false);
  };

  const fetchCompanies = async (page: number, search: string) => {
    try {
      setLoadingCompanies(true);
      const data = await apiFetchCompanies(page, search);
      
      if (page === 1) {
        setCompanies(data.companies);
      } else {
        setCompanies(prev => [...prev, ...data.companies]);
      }
      
      setHasMoreCompanies(data.has_more);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast({
        title: 'Error',
        description: 'Failed to load companies',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoadingCompanies(false);
    }
  };
  
  const fetchBoards = async (page: number, search: string) => {
    try {
      setLoadingBoards(true);
      const data = await apiFetchBoards(page, search);
      
      if (page === 1) {
        setBoards(data.boards);
      } else {
        setBoards(prev => [...prev, ...data.boards]);
      }
      
      setHasMoreBoards(data.has_more);
    } catch (error) {
      console.error('Error fetching boards:', error);
      toast({
        title: 'Error',
        description: 'Failed to load boards',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoadingBoards(false);
    }
  };
  
  useEffect(() => {
    if (isSettingsOpen) {
      fetchCompanies(1, companySearch);
      fetchBoards(1, boardSearch);
    }
  }, [isSettingsOpen]);
  
  const handleCompanySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompanySearch(e.target.value);
    setCompanyPage(1);
    fetchCompanies(1, e.target.value);
  };
  
  const handleBoardSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBoardSearch(e.target.value);
    setBoardPage(1);
    fetchBoards(1, e.target.value);
  };
  
  const handleLoadMoreCompanies = () => {
    const nextPage = companyPage + 1;
    setCompanyPage(nextPage);
    fetchCompanies(nextPage, companySearch);
  };
  
  const handleLoadMoreBoards = () => {
    const nextPage = boardPage + 1;
    setBoardPage(nextPage);
    fetchBoards(nextPage, boardSearch);
  };
  
  const handleCompanySelection = (values: string[]) => {
    setSelectedCompanies(values.map(v => parseInt(v)));
  };
  
  const handleBoardSelection = (values: string[]) => {
    setSelectedBoards(values.map(v => parseInt(v)));
  };

  const startRecording = async () => {
    try {
      if (!userId) {
        toast({
          title: 'Error',
          description: 'User ID is not available. Please refresh the page or log in again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const supportedMimeType = getSupportedMimeType();
      console.log(`Using supported MIME type: ${supportedMimeType}`);
      
      const recorder = new MediaRecorder(stream, { 
        mimeType: supportedMimeType || 'audio/webm;codecs=opus'
      });

      setAudioChunks([]);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          setAudioChunks(chunks => [...chunks, e.data]);
        }
      };

      recorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start(100);
      setMediaRecorder(recorder);
      setIsRecording(true);

      toast({
        title: 'Recording started',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: 'Recording error',
        description: 'Could not access microphone',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const stopRecording = async () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      const processingComplete = new Promise<void>((resolve) => {
        mediaRecorder.onstop = async () => {
          await new Promise(resolve => setTimeout(resolve, 100));
          
          if (audioChunks.length > 0) {
            const mimeType = mediaRecorder.mimeType || 'audio/webm;codecs=opus';
            
            const audioBlob = new Blob(audioChunks, { type: mimeType });
            console.log(`Created audio blob of type: ${mimeType}, size: ${audioBlob.size} bytes`);
            
            setAudioBlob(audioBlob);
            
            await sendAudioToBackend(audioBlob);
          } else {
            toast({
              title: 'No audio recorded',
              description: 'Please try recording again',
              status: 'warning',
              duration: 3000,
              isClosable: true,
            });
          }
          resolve();
        };

        mediaRecorder.stop();
        setIsRecording(false);

        toast({
          title: 'Recording stopped',
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
      });

      await processingComplete;
    }
  };

  const sendAudioToBackend = async (audioBlob: Blob) => {
    try {
      setIsProcessingAudio(true);
      
      if (!userId) {
        console.error('No user ID available for audio transcription');
        toast({
          title: 'Error',
          description: 'User ID is not available. Please refresh the page or log in again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      console.log('Sending audio to backend with userId:', userId);
      
      const localAudioUrl = URL.createObjectURL(audioBlob);
      
      const tempMessage: ChatMessage = {
        role: 'user',
        content: JSON.stringify({
          type: 'audio',
          audioUrl: localAudioUrl,
          transcription: null
        }),
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, tempMessage]);
      scrollToBottom();
      
      try {
        console.log("Got real conversation ID:", currentConversationId, "storing audio attachment");
        
        console.log('Using transcribeAudio from useSpeechApi');
        const result = await transcribeAudio(
          audioBlob,
          currentConversationId && !currentConversationId.startsWith('temp_') ? currentConversationId : undefined,
          userId
        );
        
        console.log('Transcription result:', result);
        
        if (result.text) {
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessageIndex = newMessages.length - 1;
            
            if (lastMessageIndex >= 0 && typeof newMessages[lastMessageIndex].content === 'string') {
              try {
                const parsedContent = JSON.parse(newMessages[lastMessageIndex].content as string);
                if (parsedContent.type === 'audio') {
                  const updatedContent = {
                    ...parsedContent,
                    transcription: result.text,
                    presigned_url: result.presigned_url || null,
                    s3Url: parsedContent.s3Url || result.s3_url || null,
                    s3_key: result.s3_key || null
                  };
                  
                  newMessages[lastMessageIndex].content = JSON.stringify(updatedContent);
                }
              } catch (e) {
                console.error('Error updating audio message:', e);
              }
            }
            
            return newMessages;
          });
          
          const chatMessageData = {
            type: 'chat',
            question: result.text,
            context: messages.slice(-10).map(msg => ({
              role: msg.role,
              content: typeof msg.content === 'string' 
                ? (isJSONString(msg.content) ? extractTextFromAudioJSON(msg.content) : msg.content) 
                : msg.content.content,
              timestamp: msg.timestamp
            })),
            user_id: userId,
            message_id: result.message_id,
            custom_prompt: savedCustomPrompt || '',
            conversation_id: result.conversation_id || currentConversationId
          };
          
          console.log("Sending chat message:", chatMessageData);
          
          sendMessage(JSON.stringify(chatMessageData));
        }
      } catch (error) {
        console.error('Error processing audio:', error);
        toast({
          title: 'Audio Processing Error',
          description: error instanceof Error ? error.message : 'Unknown error',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      
    } catch (error) {
      console.error('Error processing audio:', error);
      toast({
        title: 'Error',
        description: 'Failed to process audio recording',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsProcessingAudio(false);
    }
  };
  
  const isJSONString = (str: string): boolean => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };
  
  const extractTextFromAudioJSON = (jsonString: string): string => {
    try {
      const parsed = JSON.parse(jsonString);
      if (parsed.type === 'audio' && parsed.transcription) {
        return parsed.transcription;
      }
      return jsonString;
    } catch (e) {
      return jsonString;
    }
  };

  useEffect(() => {
    if (!lastMessage || !audioBlob) return;
    
    try {
      const data = JSON.parse(lastMessage.data);
      if (data.type === 'chat_response') {
        const realConvId = data.data.conversation_id;
      }
    } catch (e) {
      console.error('Error processing WebSocket message:', e);
    }
  }, [lastMessage, audioBlob, userId]);

  const handleToggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (connectionStatus !== 'Connected' || userId === null) return;
    
    setInput(suggestion);
    
    const isNewChat = !currentConversationId;
    
    const newMessage: ChatMessage = {
      role: 'user',
      content: suggestion,
      timestamp: new Date().toISOString()
    };

    setMessages((prev: ChatMessage[]) => [...prev, newMessage]);
    
    const context = messages.map((msg: ChatMessage) => ({
      role: msg.role,
      content: getMessageContent(msg.content),
      timestamp: msg.timestamp
    }));

    const messageData: {
      type: string;
      question: string;
      context: any[];
      user_id: number;
      custom_prompt: string;
      conversation_id?: string;
    } = {
      type: 'chat',
      question: suggestion,
      context: context.slice(-5),
      user_id: userId,
      custom_prompt: savedCustomPrompt,
    };

    if (currentConversationId) {
      messageData.conversation_id = currentConversationId;
    }

    sendMessage(JSON.stringify(messageData));
    setInput('');
    
    if (isNewChat) {
      setTimeout(() => {
        if (sidebarRef.current) {
          sidebarRef.current.fetchConversations(0, true);
        }
      }, 1000);
    }
  };

  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleRenameConversation = async (conversationId: string, newTitle: string): Promise<boolean> => {
    try {
      const success = await renameConversation(conversationId, newTitle);
      
      if (success) {
        if (currentConversationId === conversationId) {
          document.title = `${newTitle} - ${APP_NAME}`;
        }
        
        toast({
          title: "Conversation renamed",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      
      return success;
    } catch (error) {
      console.error('Error renaming conversation:', error);
      toast({
        title: "Error renaming conversation",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  };

  const handleDeleteConversation = async (conversationId: string): Promise<boolean> => {
    try {
      const success = await deleteConversation(conversationId);
      
      if (success) {
        if (currentConversationId === conversationId) {
          handleNewChat();
        }
        
        toast({
          title: "Conversation deleted",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      
      return success;
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast({
        title: "Error deleting conversation",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  };

  const Sidebar = () => (
    <Box
      bg={sidebarBg}
      w="280px"
      h="100vh"
      borderRight="1px"
      borderColor={borderColor}
      p={4}
    >
      <Button
        leftIcon={<FiPlus />}
        colorScheme="blue"
        variant="outline"
        w="100%"
        mb={4}
        onClick={handleNewChat}
      >
        New chat
      </Button>

      <VStack spacing={2} align="stretch">
        {chatSessions.map((session) => (
          <Button
            key={session.id}
            variant="ghost"
            justifyContent="flex-start"
            h="auto"
            py={2}
            px={3}
            whiteSpace="normal"
            textAlign="left"
          >
            <Text fontSize="sm" noOfLines={2}>
              {session.title}
            </Text>
          </Button>
        ))}
      </VStack>
    </Box>
  );

  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);

  const openPromptModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPromptModalOpen(true);
  };

  return (
    <Flex h="100vh" overflow="hidden" position="relative">
      <Box
        display={isSidebarOpen ? 'block' : 'none'}
        w={{ base: '100%', md: '300px' }}
        flexShrink={0}
        position={{ base: 'fixed', md: 'relative' }}
        top={0}
        bottom={0}
        left={0}
        zIndex={{ base: 20, md: 1 }}
        h="100%"
        transition="all 0.2s"
        onClick={isOpen ? onClose : undefined}
      >
        <ConversationSidebar
          onSelectConversation={(id) => {
            handleSelectConversation(id);
            if (window.innerWidth < 768) {
              setIsSidebarOpen(false);
            }
          }}
          onNewChat={handleNewChat}
          currentConversationId={currentConversationId || undefined}
          ref={sidebarRef}
          onToggleSidebar={() => setIsSidebarOpen(false)}
          onRenameConversation={handleRenameConversation}
          onDeleteConversation={handleDeleteConversation}
        />
      </Box>

      <Flex 
        direction="column" 
        flex={1} 
        overflow="hidden"
        w={{ base: '100%', md: isSidebarOpen ? "calc(100% - 300px)" : "100%" }}
        transition="width 0.2s"
        bg={messageContainerBg}
      >
        <Flex
          px={4}
          py={3}
          align="center"
          justify="space-between"
          bg={headerBg}
          borderBottom="1px"
          borderColor="gray.200"
        >
          <Flex align="center">
            {!isSidebarOpen && (
              <>
                <Tooltip label="Toggle sidebar" placement="bottom" hasArrow>
                  <IconButton
                    icon={<FiMenu />}
                    aria-label="Toggle sidebar"
                    variant="ghost"
                    onClick={() => setIsSidebarOpen(true)}
                    mr={2}
                  />
                </Tooltip>
                
                <Tooltip label="New chat" placement="bottom" hasArrow>
                  <IconButton
                    icon={<FiEdit />}
                    aria-label="New chat"
                    variant="ghost"
                    onClick={handleNewChat}
                    mr={4}
                  />
                </Tooltip>
              </>
            )}
            
            <Box mr={3}>
              <img 
                src={LOGO_URL}
                alt="IT360 Logo" 
                style={{ height: '40px', width: 'auto' }} 
              />
            </Box>
            <Text fontSize="lg" fontWeight="semibold">{APP_NAME}</Text>
            <Box
              w={2}
              h={2}
              borderRadius="full"
              bg={connectionStatus === 'Connected' ? 'green.400' : 'red.400'}
              ml={3}
            />
          </Flex>
          
          <Flex>
            <IconButton
              icon={<FiSettings />}
              aria-label="Settings"
              variant="ghost"
              onClick={handleSettingsOpen}
              size="sm"
              mr={2}
            />
            <Tooltip label="Logout" placement="bottom" hasArrow>
              <IconButton
                icon={<FiLogOut />}
                variant="ghost"
                onClick={handleLogout}
                size="sm"
                aria-label="Logout"
              />
            </Tooltip>
          </Flex>
        </Flex>

        {showDisconnected && (
          <Alert status="error">
            <AlertIcon />
            Disconnected from chat server
          </Alert>
        )}

        <Slide direction='right' in={isSettingsOpen} style={{ zIndex: 10 }}>
          <Box
            position="fixed"
            right={0}
            top={0}
            h="100vh"
            w={{ base: "100%", md: "400px" }}
            bg={settingsBg}
            boxShadow="lg"
            p={5}
            overflowY="auto"
          >
            <Flex justify="space-between" align="center" mb={6}>
              <Text fontSize="xl" fontWeight="bold">Settings</Text>
              <IconButton
                icon={<FiMenu />}
                aria-label="Close Settings"
                variant="ghost"
                onClick={handleSettingsClose}
              />
            </Flex>
            
            <VStack spacing={5} align="stretch">
              <Box>
                <Text mb={2} fontWeight="medium">Custom Prompt</Text>
                <Text fontSize="sm" color="gray.500" mb={3}>
                  Customize how the AI assistant responds to your queries
                </Text>
                <Textarea
                  value={customPrompt}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCustomPrompt(e.target.value)}
                  placeholder="Enter your custom instructions for the AI..."
                  size="md"
                  rows={5}
                  resize="vertical"
                />
              </Box>
              
              <Divider />
              
              <Box>
                <Text mb={2} fontWeight="medium">Filter by Companies</Text>
                <Text fontSize="sm" color="gray.500" mb={3}>
                  Select companies to focus your chat on
                </Text>
                
                <InputGroup size="md" mb={3}>
                  <Input
                    value={companySearch}
                    onChange={handleCompanySearch}
                    placeholder="Search companies..."
                    pr="4.5rem"
                  />
                  <InputRightElement>
                    <FiSearch />
                  </InputRightElement>
                </InputGroup>
                
                <Box maxH="150px" overflowY="auto" mb={3} borderWidth="1px" borderRadius="md" p={2}>
                  <CheckboxGroup value={selectedCompanies.map(id => id.toString())} onChange={handleCompanySelection}>
                    <Stack direction="column" spacing={1}>
                      {companies.map(company => (
                        <Checkbox key={company.id} value={company.id.toString()}>
                          {company.name}
                        </Checkbox>
                      ))}
                      {loadingCompanies && (
                        <Flex justify="center" py={2}>
                          <Spinner size="sm" />
                        </Flex>
                      )}
                    </Stack>
                  </CheckboxGroup>
                </Box>
                
                {hasMoreCompanies && !loadingCompanies && (
                  <Button 
                    size="sm" 
                    onClick={handleLoadMoreCompanies} 
                    width="100%"
                    variant="outline"
                  >
                    Load More
                  </Button>
                )}
              </Box>
              
              <Divider />
              
              <Box>
                <Text mb={2} fontWeight="medium">Filter by Boards</Text>
                <Text fontSize="sm" color="gray.500" mb={3}>
                  Select boards to focus your chat on
                </Text>
                
                <InputGroup size="md" mb={3}>
                  <Input
                    value={boardSearch}
                    onChange={handleBoardSearch}
                    placeholder="Search boards..."
                    pr="4.5rem"
                  />
                  <InputRightElement>
                    <FiSearch />
                  </InputRightElement>
                </InputGroup>
                
                <Box maxH="150px" overflowY="auto" mb={3} borderWidth="1px" borderRadius="md" p={2}>
                  <CheckboxGroup value={selectedBoards.map(id => id.toString())} onChange={handleBoardSelection}>
                    <Stack direction="column" spacing={1}>
                      {boards.map(board => (
                        <Checkbox key={board.id} value={board.id.toString()}>
                          {board.name}
                        </Checkbox>
                      ))}
                      {loadingBoards && (
                        <Flex justify="center" py={2}>
                          <Spinner size="sm" />
                        </Flex>
                      )}
                    </Stack>
                  </CheckboxGroup>
                </Box>
                
                {hasMoreBoards && !loadingBoards && (
                  <Button 
                    size="sm" 
                    onClick={handleLoadMoreBoards} 
                    width="100%"
                    variant="outline"
                  >
                    Load More
                  </Button>
                )}
              </Box>
              
              <Flex justify="flex-end" mt={4}>
                <Button 
                  variant="outline" 
                  mr={3} 
                  onClick={handleSettingsClose}
                >
                  Cancel
                </Button>
                <Button 
                  colorScheme="blue" 
                  onClick={handleSaveSettings}
                >
                  Save
                </Button>
              </Flex>
            </VStack>
          </Box>
        </Slide>

        <VStack
          flex={1}
          overflowY="auto"
          spacing={4}
          p={4}
          bg={messageContainerBg}
          css={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: '#cbd5e0',
              borderRadius: '24px',
            },
          }}
          position="relative"
        >
          {messages.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              flex={1}
              w="100%"
            >
              <Text fontSize="xl" mb={2} fontWeight="medium" color="blue.500">Welcome to IT Support Assistant</Text>
              <Text fontSize="md" mb={6} color="gray.600">How can I help you today?</Text>
              
              <Box
                w="100%"
                maxW="900px"
                mb={8}
              >
                <Text fontSize="md" mb={4} fontWeight="bold" color="blue.600" textAlign="center">
                  Suggested prompts:
                </Text>
                
                <Box 
                  display="grid" 
                  gridTemplateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
                  gridTemplateRows="repeat(2, auto)"
                  gap={5}
                  mb={2}
                >
                  {suggestionGroups.flatMap((group, groupIndex) => 
                    (groupIndex === 0 && !expandedGroups.includes(0) ? group.items.slice(0, 3) : group.items).map((item, itemIndex) => (
                      <Box
                        key={`${groupIndex}-${itemIndex}`}
                        bg={cardBg}
                        borderRadius="md"
                        p={5}
                        minHeight="130px"
                        boxShadow="sm"
                        cursor="pointer"
                        transition="all 0.2s"
                        _hover={{ 
                          transform: 'scale(1.03)',
                          boxShadow: 'md',
                          bg: cardHoverBg
                        }}
                        onClick={() => handleSuggestionClick(item.text)}
                      >
                        <Flex align="center" mb={3}>
                          <Box mr={2} color={groupIndex === 0 ? "blue.500" : "blue.600"}>
                            <item.icon size={20} />
                          </Box>
                          <Text 
                            fontWeight="bold" 
                            fontSize="md"
                            color={groupIndex === 0 ? "blue.500" : "blue.600"}
                          >
                            {group.name}
                          </Text>
                        </Flex>
                        <Text fontSize="sm" fontWeight="medium" color="gray.600" pl="26px">
                          {item.text}
                        </Text>
                      </Box>
                    ))
                  )}
                </Box>
                
                <Flex justify="flex-end">
                  <Text 
                    fontSize="sm" 
                    color="blue.500" 
                    cursor="pointer"
                    fontWeight="medium"
                    _hover={{ textDecoration: "underline" }}
                    onClick={openPromptModal}
                  >
                    View all prompts
                  </Text>
                </Flex>
              </Box>
            </Flex>
          ) : (
            messages.map((msg: ChatMessage, idx: number) => (
              <Message
                key={idx}
                message={msg.content}
                isUser={msg.role === 'user'}
                timestamp={msg.timestamp}
                onFeedback={handleFeedback}
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </VStack>

        <Box 
          p={{ base: 2, md: 3 }}
          pb={{ base: 3, md: 5 }}
          bg={messageContainerBg}
          position="relative"
          borderTop="0"
        >
          {messages.length > 0 && (
            <Flex 
              maxW="3xl"
              mx="auto"
              w="100%"
              px={4}
              mb={2}
              justify="flex-end"
            >
              <Flex
                alignItems="center"
                cursor="pointer"
                color="blue.500"
                fontSize="sm"
                fontWeight="medium"
                onClick={openPromptModal}
                _hover={{ textDecoration: "underline" }}
              >
                <Box mr={1}><FiZap /></Box>
                <Text>View suggestion prompts</Text>
              </Flex>
            </Flex>
          )}
          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <Flex 
              maxW="3xl"
              mx="auto"
              w="100%"
              px={4}
            >
              <Box 
                w="100%" 
                position="relative"
                borderRadius="2xl"
                border="1px solid"
                borderColor="rgba(0, 0, 0, 0.1)"
                bg={inputBg}
                boxShadow="0 0 15px rgba(0,0,0,0.05)"
                _dark={{
                  borderColor: "rgba(255, 255, 255, 0.1)"
                }}
                overflow="hidden"
              >
                <Textarea
                  value={input}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInput(e.target.value)}
                  placeholder={isProcessingAudio ? "Transcribing audio..." : "Type your message..."}
                  disabled={connectionStatus !== 'Connected' || isProcessingAudio}
                  bg="transparent"
                  borderRadius="2xl"
                  minH="80px"
                  maxH="200px"
                  rows={2}
                  resize="none"
                  w="100%"
                  border="none"
                  px={5}
                  pt={4}
                  pb={12}
                  fontSize="md"
                  _focus={{
                    border: "none",
                    boxShadow: "none",
                    outline: "none"
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (input.trim() && connectionStatus === 'Connected' && !isProcessingAudio) {
                        handleSubmit(e as any);
                      }
                    }
                  }}
                />
                
                {isProcessingAudio && (
                  <Box position="absolute" bottom="0" left="0" right="0" height="2px">
                    <Progress size="xs" isIndeterminate colorScheme="blue" />
                  </Box>
                )}
                
                <Flex 
                  position="absolute" 
                  right="10px" 
                  bottom="10px"
                  zIndex="1"
                  alignItems="center"
                  gap={2}
                >
                  <IconButton
                    size="md"
                    icon={isRecording ? <FiMicOff /> : <FiMic />}
                    aria-label={isRecording ? "Stop recording" : "Start recording"}
                    onClick={handleToggleRecording}
                    disabled={connectionStatus !== 'Connected' || isProcessingAudio}
                    variant="ghost"
                    colorScheme={isRecording ? "red" : "gray"}
                    borderRadius="full"
                  />
                  <IconButton
                    size="md"
                    icon={<FiSend />}
                    colorScheme="blue"
                    variant={input.trim() ? "solid" : "ghost"}
                    type="submit"
                    disabled={connectionStatus !== 'Connected' || !input.trim() || isProcessingAudio}
                    aria-label="Send message"
                    borderRadius="full"
                  />
                </Flex>
              </Box>
            </Flex>
          </form>
        </Box>
      </Flex>

      <PromptModal
        isOpen={isPromptModalOpen}
        onClose={() => setIsPromptModalOpen(false)}
        suggestionGroups={suggestionGroups}
        onSelectPrompt={handleSuggestionClick}
      />
    </Flex>
  );
};

export default ChatInterface; 